import {useEffect, useState, useRef} from 'react'
import {UserModel} from '../../auth'
import {getOfferPerformance} from '../../offers/cors/_requests'
import {Link} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {Tooltip} from 'bootstrap'

type Props = {
  className: string
  language: string
  currentUser: UserModel | undefined
}

/**
 * Green dashboard card — the supplier's offer book at a glance.
 *
 * Multi-supplier: leads with ACTIVE OFFERS (distinct sellable products the
 * supplier has a live offer on) and shows WINNING OFFERS (buy-box wins)
 * underneath, replacing the old "active products" catalog-ownership count.
 * Both numbers come from /offers/performance (offer_model->getCounts) — the
 * SAME sellable-scoped source as the My Offers summary strip and WinRateCard,
 * so the figures agree everywhere in the portal.
 */
const ActiveProductsCard: React.FC<Props> = ({language, currentUser, className}) => {
  const {formatMessage} = useIntl()
  const tooltipTriggerRef = useRef<HTMLSpanElement>(null)
  const [state, setState] = useState<{active_offers: number; winning_offers: number}>({
    active_offers: 0,
    winning_offers: 0,
  })

  useEffect(() => {
    const getData = async () => {
      if (!currentUser?.token) return
      const res = await getOfferPerformance(currentUser.token, language)
      setState({
        active_offers: res.data?.data?.active_offers ?? 0,
        winning_offers: res.data?.data?.winning_offers ?? 0,
      })
    }
    getData()
  }, [language, currentUser])

  useEffect(() => {
    if (tooltipTriggerRef.current) {
      const existingTooltip = Tooltip.getInstance(tooltipTriggerRef.current)
      if (existingTooltip) {
        existingTooltip.dispose()
      }
      new Tooltip(tooltipTriggerRef.current, {
        html: true,
        customClass: 'tooltip-light',
      })
    }
    return () => {
      if (tooltipTriggerRef.current) {
        const existingTooltip = Tooltip.getInstance(tooltipTriggerRef.current)
        if (existingTooltip) {
          existingTooltip.dispose()
        }
      }
    }
  }, [language])

  return (
    <div className={`hsb-card ${className}`}>
      <div>
        <div className='hsb-card-head'>
          <span className='hsb-chip hsb-chip--violet'>
            <span className='material-symbols-outlined'>sell</span>
          </span>
        </div>
        <span className='hsb-kpi-label'>
          {formatMessage({id: 'active_offers'})}
          <span
            ref={tooltipTriggerRef}
            data-bs-toggle='tooltip'
            data-bs-placement='top'
            data-bs-custom-class='tooltip-light'
            data-bs-html='true'
            title={formatMessage({id: 'active_offers_info'})}
          >
            <i className='fas fa-info-circle hsb-info-icon'></i>
          </span>
        </span>
        <h2 className='hsb-kpi-value'>{state.active_offers}</h2>
        {/* Buy-box wins among the active offers above — same getCounts
            source as the My Offers summary strip and WinRateCard. */}
        <span className='hsb-kpi-sub hsb-kpi-sub--success'>
          {formatMessage({id: 'winning_offers_subline'}, {count: state.winning_offers})}
        </span>
      </div>

      <Link to='/offers/list' className='hsb-link hsb-link--violet'>
        {formatMessage({id: 'view_offers'})}
        <span className='material-symbols-outlined'>verified</span>
      </Link>
    </div>
  )
}

export default ActiveProductsCard
